import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Paperclip, Send, Loader2, CheckCircle, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import { api } from '../../services/api';

const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const formaterDateHeure = (valeur) =>
  valeur
    ? new Date(valeur).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
    : '—';

/**
 * Les observations reçues sur une caisse, et la réponse de la gestionnaire.
 *
 * CE QUE CET ÉCRAN REMPLACE
 * -------------------------
 * Un `alert()` natif, avec émojis, dont le texte se terminait par « Veuillez
 * contacter votre superviseur pour y répondre ». La table portait pourtant
 * `reponse`, `repondu_par`, `date_reponse`, et la route
 * `PUT /observation/:id/repondre` existait déjà côté serveur : la boucle était
 * construite aux trois quarts et s'arrêtait juste avant la réponse.
 *
 * Répondre débloque la validation de la caisse — c'est la seule issue.
 */
const ModalObservationsCaisse = ({ ouvert, onFermer, caisseCentraleId, dateCaisse, onRepondu }) => {
  const [observations, setObservations] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Un brouillon par observation : deux questions distinctes appellent deux
  // réponses distinctes, et un champ partagé les mélangerait.
  const [brouillons, setBrouillons] = useState({});
  // Un seul envoi à la fois, et l'on sait LEQUEL : sans l'identifiant, toutes
  // les lignes tourneraient ensemble.
  const [envoiEnCours, setEnvoiEnCours] = useState(null);

  const charger = useCallback(async () => {
    if (!caisseCentraleId) return;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await api.caisseCentrale.getObservations(caisseCentraleId);
      if (reponse.success) {
        setObservations(Array.isArray(reponse.data) ? reponse.data : []);
      } else {
        setErreur(reponse.message || 'Impossible de charger les observations.');
      }
    } catch (e) {
      console.error('Erreur chargement observations :', e);
      setErreur('Impossible de charger les observations.');
    } finally {
      setChargement(false);
    }
  }, [caisseCentraleId]);

  useEffect(() => {
    if (ouvert) charger();
  }, [ouvert, charger]);

  const repondre = async (observation) => {
    const texte = String(brouillons[observation.id] || '').trim();
    if (!texte) return;

    try {
      setEnvoiEnCours(observation.id);
      const reponse = await api.caisseCentrale.repondreObservation(observation.id, texte);
      if (reponse.success) {
        setBrouillons((prev) => ({ ...prev, [observation.id]: '' }));
        await charger();
        // L'écran appelant rafraîchit le statut de la caisse : répondre à la
        // dernière observation la fait repasser en attente de validation.
        onRepondu?.(reponse.data?.nb_observations_restantes ?? null);
      } else {
        setErreur(reponse.message || "La réponse n'a pas été enregistrée.");
      }
    } catch (e) {
      console.error('Erreur réponse observation :', e);
      setErreur("La réponse n'a pas été enregistrée.");
    } finally {
      setEnvoiEnCours(null);
    }
  };

  const enAttente = observations.filter((o) => o.statut !== 'resolu');

  return (
    <Dialog open={Boolean(ouvert)} onOpenChange={(o) => !o && onFermer?.()}>
      <DialogContent className="flex w-[95vw] max-w-3xl flex-col gap-0 overflow-hidden p-0 !max-h-[92vh]">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#c66d59] text-white">
            <MessageSquare size={18} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Observations sur votre caisse
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              {dateCaisse
                ? `Caisse du ${new Date(dateCaisse).toLocaleDateString('fr-FR')}. `
                : ''}
              Votre réponse débloque la validation.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {chargement && (
            <div className="flex h-40 items-center justify-center">
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#c66d59] border-t-transparent" />
                <span className="font-medium text-[#c66d59]">Chargement…</span>
              </div>
            </div>
          )}

          {!chargement && erreur && (
            <div className="rounded border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-800">
              {erreur}
            </div>
          )}

          {!chargement && !erreur && observations.length === 0 && (
            <EtatVide
              icone={CheckCircle}
              titre="Aucune observation"
              description="Votre caisse n'a reçu aucune remarque de la caisse centrale."
            />
          )}

          {!chargement && observations.map((observation) => {
            const estResolue = observation.statut === 'resolu';
            const estJustificatif = observation.type === 'justificatif';

            return (
              <div
                key={observation.id}
                className={`rounded-lg border-2 ${estResolue ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-inherit px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                    {estJustificatif ? <Paperclip size={15} /> : <MessageSquare size={15} />}
                    {estJustificatif ? 'Justificatif demandé' : 'Observation'}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${estResolue ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                  >
                    {estResolue ? <CheckCircle size={12} /> : <Clock size={12} />}
                    {estResolue ? 'Répondue' : 'En attente de votre réponse'}
                  </span>
                </div>

                <div className="space-y-3 px-4 py-3">
                  <div>
                    <p className="text-sm text-gray-800">{observation.message}</p>
                    <p className="mt-1 text-[11px] text-gray-500">
                      {[observation.emis_par_prenom, observation.emis_par_nom].filter(Boolean).join(' ') || 'Caisse centrale'}
                      <span className="mx-1.5 text-gray-400">•</span>
                      {formaterDateHeure(observation.date_emission)}
                    </p>
                  </div>

                  {estResolue ? (
                    <div className="rounded-lg border border-emerald-200 bg-white p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                        Votre réponse
                      </p>
                      <p className="mt-1 text-sm text-gray-800">{observation.reponse}</p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {formaterDateHeure(observation.date_reponse)}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Votre réponse <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={3}
                        value={brouillons[observation.id] || ''}
                        onChange={(e) => setBrouillons((prev) => ({ ...prev, [observation.id]: e.target.value }))}
                        placeholder="Expliquez, ou indiquez où trouver la pièce demandée…"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                      />
                      <div className="mt-2 flex justify-end">
                        <Button
                          size="sm"
                          className="bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50"
                          disabled={
                            envoiEnCours === observation.id ||
                            !String(brouillons[observation.id] || '').trim()
                          }
                          onClick={() => repondre(observation)}
                        >
                          {envoiEnCours === observation.id ? (
                            <>
                              <Loader2 size={15} className="mr-2 animate-spin" />
                              Envoi…
                            </>
                          ) : (
                            <>
                              <Send size={15} className="mr-2" />
                              Répondre
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          {enAttente.length > 0 && (
            <p className="mr-auto self-center text-[11px] text-amber-700">
              {enAttente.length} observation{enAttente.length > 1 ? 's' : ''} sans réponse
            </p>
          )}
          <Button variant="outline" className={BOUTON_PIED_CLASSES} onClick={onFermer}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalObservationsCaisse;
